import React, { ForwardedRef, forwardRef, PropsWithChildren } from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { BasicIcon, IconType } from '../icons/BasicIcon';

const WaitingIcon = ({ color }: { color: string }) => (
  <Box width="1em" height="1em" display="flex" alignItems="center" justifyContent="center">
    <CircularProgress sx={{ color }} size="75%" />
  </Box>
);

const getIcon = (icon?: BasicButtonProps['startIcon'] | BasicButtonProps['endIcon']) =>
  typeof icon === 'undefined' || icon === null ? undefined : typeof icon === 'number' ? (
    <BasicIcon type={icon} />
  ) : (
    icon
  );

export type BasicButtonProps = PropsWithChildren<{
  onClick?: MuiButtonProps['onClick'];
  color?: Exclude<MuiButtonProps['color'], 'inherit'>;
  variant?: MuiButtonProps['variant'];
  size?: MuiButtonProps['size'];
  sx?: MuiButtonProps['sx'];
  startIcon?: MuiButtonProps['startIcon'] | IconType;
  endIcon?: MuiButtonProps['endIcon'] | IconType;
  disabled?: MuiButtonProps['disabled'];
  type?: MuiButtonProps['type'];
  component?: string;
  loading?: boolean;
  id?: string;
}>;

const BasicButton = forwardRef(function BasicButton(
  {
    children,
    sx,
    onClick,
    disabled,
    color,
    size = 'medium',
    variant = 'outlined',
    loading,
    startIcon,
    endIcon,
    id,
    ...muiButtonProps
  }: BasicButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const theme = useTheme();
  const palette = color ? theme.palette[color] : theme.palette.primary;
  const startIconToUse = getIcon(startIcon);
  const endIconToUse = getIcon(endIcon);

  return (
    <MuiButton
      id={id}
      ref={ref}
      color={color || 'primary'}
      variant={variant}
      size={size}
      disabled={disabled || loading}
      onClick={onClick}
      startIcon={loading && startIconToUse ? <WaitingIcon color={palette.main} /> : startIconToUse}
      endIcon={loading && endIconToUse ? <WaitingIcon color={palette.main} /> : endIconToUse}
      {...muiButtonProps}
      sx={[
        {
          whiteSpace: 'nowrap',
          px: '15px',
          height: size === 'large' ? '40px' : undefined, // to make the large button equally high as a small input field
          borderRadius: '20px',
          boxShadow: 'none',
          outline: 'none',
          '&:focus': {
            boxShadow: 'none',
            outline: 'none',
          },
          '&:hover': {
            outline: 'none',
            boxShadow: 'none',
            backgroundColor: variant === 'outlined' ? palette.main : variant === 'contained' ? palette.dark : undefined,
            color: variant === 'text' ? undefined : theme => theme.palette.primary.contrastText,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
        {
          position: 'relative',
        },
      ]}
    >
      {children}
      {loading && !startIconToUse && !endIconToUse && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress sx={{ color: palette.main }} size={20} />
        </Box>
      )}
    </MuiButton>
  );
});

export { BasicButton };
